import { useContext, useEffect, useState } from "react"
import Grid from "@mui/material/Grid2";
import { pointsOfInterestLayer, view, setHighlightedPointOfInterest, getPointOfInterest } from "../../utilities/buffaloArcGisHelper";
import Graphic from "@arcgis/core/Graphic";
import { IconButton } from "@mui/material";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { rootContext } from "../../contexts/rootContext";

type SelectedPointOfInterest = {
    index: number
    name: string
    description?: string
    source?: string
}

const sourceLabels: Record<string, string> = {
    "ozark society": "Source: The Buffalo River Handbook, Ozark Society"
}

// Descriptions are authored as multi-line template literals, so the indentation
// they carry is stripped and blank lines are turned into separate paragraphs.
const getParagraphs = (description: string) =>
    description
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
        .filter((paragraph) => paragraph.length > 0)

interface PointOfInterestDivProps {
    isTripDetailsOpen: boolean
}

export const PointOfInterestDiv = (props: PointOfInterestDivProps) => {
    const context = useContext(rootContext)
    const [selectedPointOfInterest, setSelectedPointOfInterest] = useState<SelectedPointOfInterest>()

    useEffect(() => {
        const tryToInitHitTest = () => {
            if (view !== null) {
                view.on("click", async (event) => {
                    const hit = await view?.hitTest(event)
                    const result: any = hit?.results.find((result) =>
                        result.type === "graphic" &&
                        result.graphic.layer === pointsOfInterestLayer
                    )
                    if (result == null) return

                    const index = (result.graphic as Graphic).attributes.Index
                    const pointOfInterest = getPointOfInterest(index)
                    if (pointOfInterest === undefined) return

                    setSelectedPointOfInterest({ index, ...pointOfInterest })
                })
            } else {
                setTimeout(tryToInitHitTest, 250)
            }
        }
        setTimeout(tryToInitHitTest, 250)
    }, [])

    // The card sits above the trip details page, so it is closed rather than
    // left showing over it.
    useEffect(() => {
        if (props.isTripDetailsOpen)
            setSelectedPointOfInterest(undefined)
    }, [props.isTripDetailsOpen])

    useEffect(() => {
        setHighlightedPointOfInterest(selectedPointOfInterest?.index ?? null)
    }, [selectedPointOfInterest])

    const handleClose = () => {
        setSelectedPointOfInterest(undefined)
    }

    if (selectedPointOfInterest === undefined) return <></>

    const paragraphs = getParagraphs(selectedPointOfInterest.description ?? "")
    const sourceLabel = selectedPointOfInterest.source !== undefined ? sourceLabels[selectedPointOfInterest.source] : undefined

    return (
    <Grid
        container
        sx={{
            position: "absolute",
            top: "70px",
            right: "10px",
            left: { xs: "10px", sm: "auto" },
            backgroundColor: "black",
            color: "white",
            padding: "10px",
            borderRadius: "15px",
            width: { xs: "auto", sm: "400px" },
            maxWidth: "400px",
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            zIndex: 1400,
            userSelect: "none"
        }}
        spacing={1}
    >
        <Grid
            container
            size={12}
            sx={{
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "nowrap"
            }}
        >
            <Grid sx={{color: context.theme.text.primary}}>
                {selectedPointOfInterest.name}
            </Grid>
            <Grid>
                <IconButton onClick={()=>handleClose()} sx={{color: context.theme.text.contrast4}}>
                    <CloseOutlinedIcon/>
                </IconButton>
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            {paragraphs.length > 0 ?
                paragraphs.map((paragraph, index) =>
                    <Grid size={12} key={index} sx={{color: context.theme.text.contrast5}}>
                        {paragraph}
                    </Grid>
                )
                :
                <Grid size={12} sx={{color: context.theme.text.contrast2}}>
                    No description available.
                </Grid>
            }
            {sourceLabel !== undefined &&
                <Grid size={12} sx={{color: context.theme.text.contrast2, fontSize: "12px"}}>
                    {sourceLabel}
                </Grid>
            }
        </Grid>
    </Grid>)
}
